import { AccountCredit } from "../components/AccountCredit";
import { AssistantCalls } from "./settings/PhoneAssistantTab";
import { HoldBadge } from "../components/HoldBadge";
import { AddressField } from "../components/AddressField";
import { CardLinkButtons, ClientPlansTab } from "../components/ClientPlans";
import type { AppointmentView, FeeOwed, AuditEntry, ClientPhoneView, TextNumber, ChartSummary, HealthSummary, MessageView, PaymentView, SavedCardView, SubmissionDetail, SubmissionView, ClientAddressInput, ClientDetail, ClientInput, ClientSummary, ConsentChannel, ImportedRecordSummary, PastVisitView } from "@vaulted/shared";
import { SOURCE_LABELS, ADDRESS_KINDS, ADDRESS_KIND_LABELS, PAYMENT_METHOD_LABELS, STATUS_LABELS, formatCents, AUDIT_ACTIONS, CONSENT_LABELS, SEX_AT_BIRTH_LABELS, ageOn, formatPhone, normalizePhone } from "@vaulted/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { del, errorMessage, get, post, put } from "../api";
import { DateOfBirthField, Alert, Badge, Button, Checkbox, Dialog, Field, Loading, Panel, PanelHeader, SelectField, TabButtons, TextAreaField, cx, formatDateTime, formatDateTime24 } from "../components/ui";
import { SignatureImage } from "../components/SignatureInput";
import { GfeBadge, GfeTab } from "../components/Gfe";
import { FormsBadge, STATUS_TONE, time12 } from "../components/AppointmentDialog";
import { BookingDialog } from "../components/BookingDialog";
import { FormBody } from "../components/FormsFill";
import { HealthBlock } from "../components/HealthBlock";
import { useTextNumbers } from "../components/TextNumberSelect";
import { ExcuseNotesList } from "../components/ExcuseNotes";
import { PastVisitRow } from "../components/PastVisits";
import { SourceBadge } from "../components/SourceBadge";
import { DirectionTag, isPair, pairTextAndEmail } from "../components/MessageKind";
import { useSession } from "../session";
import { ClientCalls } from "./Voicemail";
import { todayIn, useCalendar } from "./Calendar";
import { ClientRows, TagChips, displayName, fmtDob, useClientSearch, useTags } from "./Clients";
import { AddButton, RemoveButton } from "./settings/common";
import { BackLink, useBackTitle } from "../components/BackLink";

const today = () => todayIn();
type Tab = "details" | "visits" | "forms" | "gfe" | "charts" | "plans" | "payments" | "messages" | "notes" | "family" | "activity";

export function ClientProfilePage() {
  const { id = "" } = useParams();
  const { can, me } = useSession();
  const qc = useQueryClient();
  const tags = useTags();
  const data = useQuery({ queryKey: ["client", id], queryFn: () => get<ClientDetail>(`/api/clients/${id}`), staleTime: 60_000 });
  const [tab, setTab] = useState<Tab>("details");
  const [dialog, setDialog] = useState<"tags" | "merge" | "book" | null>(null);
  useBackTitle(data.data ? displayName(data.data) : null);
  const setClient = (d: ClientDetail) => {
    qc.setQueryData(["client", id], d);
    void qc.invalidateQueries({ queryKey: ["clients"] });
  };
  const archive = useMutation({
    mutationFn: () => post<ClientDetail>(`/api/clients/${id}/${data.data?.status === "archived" ? "restore" : "archive"}`),
    onSuccess: setClient,
  });

  if (data.isLoading) return <Loading />;
  if (data.error || !data.data) return <Alert>{errorMessage(data.error)}</Alert>;
  const c = data.data;
  const age = c.dob ? ageOn(c.dob, today()) : null;
  const guardians = c.links.filter((l) => l.role === "guardian");
  const editable = can("clients.edit") && c.status !== "merged";

  return (
    <div className="max-w-4xl">
      <BackLink to="/clients" label="Clients" />
      <header className="mt-2 mb-4 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="page-title">{displayName(c)}</h1>
          <p className="mt-1 text-ink-soft">
            {c.dob ? `${fmtDob(c.dob)} · age ${age}` : <span className="font-semibold text-wait">Birth date needed</span>}
            {c.mobile && ` · ${formatPhone(c.mobile)}`}
            {c.email && ` · ${c.email}`}
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            {c.status === "archived" && <Badge tone="off">Archived</Badge>}
            {c.status === "lead" && <Badge tone="wait">Lead</Badge>}
            {age !== null && age < 18 && <Badge tone={guardians.length ? "brand" : "wait"}>{guardians.length ? "Minor" : "Minor: no guardian linked"}</Badge>}
            {age !== null && age >= 18 && guardians.length > 0 && <Badge tone="wait">Turned 18: needs own contact and consent</Badge>}
            <TagChips ids={c.tagIds} tags={tags.data} />
            {editable && (
              <button className="text-sm font-semibold text-brand hover:text-brand-deep" onClick={() => setDialog("tags")}>
                {c.tagIds.length ? "Edit tags" : "+ Tags"}
              </button>
            )}
          </div>
        </div>
        {c.status !== "merged" && (
          <div className="flex flex-wrap gap-2">
            {can("appointments.manage") && c.status !== "archived" && (
              <Button variant="primary" onClick={() => setDialog("book")}>
                Book
              </Button>
            )}
            {can("clients.merge") && <Button onClick={() => setDialog("merge")}>Merge a duplicate</Button>}
            {editable && (
              <Button variant={c.status === "archived" ? "secondary" : "quiet"} busy={archive.isPending} onClick={() => archive.mutate()}>
                {c.status === "archived" ? "Restore" : "Archive"}
              </Button>
            )}
          </div>
        )}
      </header>

      {c.status === "merged" && c.mergedIntoId && (
        <div className="mb-4">
          <Alert tone="info">
            This record was merged into another one. <Link to={`/clients/${c.mergedIntoId}`} className="font-semibold underline">Open the current record</Link>.
          </Alert>
        </div>
      )}
      {c.possibleDuplicateOf && <PossibleDuplicate c={c} onSaved={setClient} />}
      <FeesOwedBanner clientId={c.id} />
      {c.schedulingAlert && (
        <div className="mb-4 rounded-[var(--radius-control)] border border-wait/40 bg-wait-wash px-4 py-2.5 text-sm font-semibold text-wait" role="note">
          ⚠ {c.schedulingAlert}
        </div>
      )}
      {archive.error && <Alert>{errorMessage(archive.error)}</Alert>}

      <TabButtons
        label="Client sections"
        value={tab}
        onChange={setTab}
        items={[
          ["details", "Details"],
          ["visits", "Visits"],
          ["forms", "Forms"],
          ["gfe", "Good faith exam"],
          ["charts", "Charts"],
          ["plans", "Plans"],
          ["payments", "Payments"],
          ["messages", "Communications"],
          ["notes", `Notes (${c.notes.length})`],
          ["family", `Family (${c.links.length})`],
          ...(can("audit.view") ? ([["activity", "Activity"]] as const) : []),
        ]}
      />

      {tab === "details" && <DetailsTab c={c} editable={editable} onSaved={setClient} />}
      {tab === "visits" && (
        <div className="flex flex-col gap-6">
          <VisitsTab clientId={c.id} />
          <ExcuseNotesList clientId={c.id} />
        </div>
      )}
      {tab === "forms" && <FormsTab clientId={c.id} minor={!!c.dob && ageOn(c.dob, today()) < 18} />}
      {tab === "gfe" && <GfeTab clientId={c.id} />}
      {tab === "charts" && <ChartsTab clientId={c.id} />}
      {tab === "plans" && <ClientPlansTab clientId={c.id} clientName={displayName(c)} />}
      {tab === "payments" && <PaymentsTab clientId={c.id} />}
      {tab === "messages" && <MessagesTab clientId={c.id} />}
      {tab === "notes" && <NotesTab c={c} onSaved={setClient} myName={me ? `${me.staff.firstName} ${me.staff.lastName}` : ""} />}
      {tab === "family" && <FamilyTab c={c} editable={editable} onSaved={setClient} />}
      {tab === "activity" && <ActivityTab clientId={c.id} />}

      {dialog === "tags" && <TagsDialog c={c} onClose={() => setDialog(null)} onSaved={setClient} />}
      {dialog === "merge" && <MergeDialog c={c} onClose={() => setDialog(null)} onSaved={setClient} />}
      {dialog === "book" && <BookFor c={c} onClose={() => setDialog(null)} />}
    </div>
  );
}

const toInput = (c: ClientDetail): ClientInput => ({
  firstName: c.firstName,
  lastName: c.lastName,
  preferredName: c.preferredName,
  dob: c.dob,
  sexAtBirth: c.sexAtBirth,
  pronouns: c.pronouns,
  mobile: c.mobile ? formatPhone(c.mobile) : null,
  email: c.email,
  emergencyName: c.emergencyName,
  emergencyRelationship: c.emergencyRelationship,
  emergencyPhone: c.emergencyPhone ? formatPhone(c.emergencyPhone) : null,
  schedulingAlert: c.schedulingAlert,
  addresses: c.addresses.map(({ id: _id, ...a }) => a),
});

function DetailsTab({ c, editable, onSaved }: { c: ClientDetail; editable: boolean; onSaved: (d: ClientDetail) => void }) {
  const [v, setV] = useState<ClientInput>(toInput(c));
  const [notice, setNotice] = useState<string | null>(null);
  useEffect(() => setV(toInput(c)), [c]);
  const set = <K extends keyof ClientInput>(k: K, value: ClientInput[K]) => {
    setNotice(null);
    setV((cur) => ({ ...cur, [k]: value }));
  };
  const save = useMutation({
    mutationFn: () => put<ClientDetail>(`/api/clients/${c.id}`, v),
    onSuccess: (d) => {
      onSaved(d);
      setNotice("Saved.");
    },
  });
  const consentOff = useMutation({
    mutationFn: (channel: ConsentChannel) => post<ClientDetail>(`/api/clients/${c.id}/consents`, { channel, granted: false }),
    onSuccess: onSaved,
  });
  const dirty = JSON.stringify(v) !== JSON.stringify(toInput(c));
  const setAddr = (i: number, patch: Partial<ClientAddressInput>) => set("addresses", v.addresses.map((a, j) => (j === i ? { ...a, ...patch } : a)));

  return (
    <div className="flex flex-col gap-6">
      <form
        className="flex flex-col gap-6"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        <fieldset disabled={!editable} className="flex flex-col gap-6">
          <Panel>
            <PanelHeader title="Personal" />
            <div className="grid gap-4 px-5 py-5 sm:grid-cols-2">
              <Field label="First name" required maxLength={80} value={v.firstName} onChange={(e) => set("firstName", e.target.value)} />
              <Field label="Last name" required maxLength={80} value={v.lastName} onChange={(e) => set("lastName", e.target.value)} />
              <Field label="Preferred name (optional)" maxLength={80} value={v.preferredName ?? ""} onChange={(e) => set("preferredName", e.target.value || null)} />
              <DateOfBirthField required={!!c.dob} value={v.dob ?? ""} onChange={(d) => set("dob", d || null)} hint={c.dob ? undefined : "Needed before they can be booked."} />
              <SelectField label="Sex assigned at birth" value={v.sexAtBirth ?? ""} onChange={(e) => set("sexAtBirth", (e.target.value || null) as ClientInput["sexAtBirth"])}>
                <option value="">—</option>
                {Object.entries(SEX_AT_BIRTH_LABELS).map(([k, l]) => (
                  <option key={k} value={k}>
                    {l}
                  </option>
                ))}
              </SelectField>
              <Field label="Pronouns (optional)" maxLength={40} value={v.pronouns ?? ""} onChange={(e) => set("pronouns", e.target.value || null)} />
              <Field label="Mobile" type="tel" value={v.mobile ?? ""} onChange={(e) => set("mobile", e.target.value || null)} />
              <Field label="Email" type="email" value={v.email ?? ""} onChange={(e) => set("email", e.target.value || null)} />
              <Field
                label="Scheduling alert"
                className="sm:col-span-2"
                maxLength={200}
                placeholder="e.g. Hard stick: use 24G"
                value={v.schedulingAlert ?? ""}
                onChange={(e) => set("schedulingAlert", e.target.value || null)}
                hint="Shown whenever they're booked or checked in."
              />
            </div>
          </Panel>

          <Panel>
            <PanelHeader title="Emergency contact" />
            <div className="grid gap-4 px-5 py-5 sm:grid-cols-3">
              <Field label="Name" maxLength={120} value={v.emergencyName ?? ""} onChange={(e) => set("emergencyName", e.target.value || null)} />
              <Field label="Relationship" maxLength={60} value={v.emergencyRelationship ?? ""} onChange={(e) => set("emergencyRelationship", e.target.value || null)} />
              <Field label="Phone" type="tel" value={v.emergencyPhone ?? ""} onChange={(e) => set("emergencyPhone", e.target.value || null)} />
            </div>
          </Panel>

          <Panel>
            <PanelHeader title="Addresses" description="Home, and places they get mobile visits." />
            <div className="flex flex-col gap-4 px-5 py-5">
              {v.addresses.length === 0 && <p className="text-sm text-muted">No addresses.</p>}
              {v.addresses.map((a, i) => (
                <div key={i} className="grid gap-3 rounded-[var(--radius-control)] border border-line p-3 sm:grid-cols-6">
                  <Field label="Label" className="sm:col-span-2" placeholder="Home" maxLength={40} value={a.label} onChange={(e) => setAddr(i, { label: e.target.value })} />
                  <SelectField label="Type" className="sm:col-span-2" value={a.kind} onChange={(e) => setAddr(i, { kind: e.target.value as ClientAddressInput["kind"] })}>
                    {ADDRESS_KINDS.map((k) => (
                      <option key={k} value={k}>
                        {ADDRESS_KIND_LABELS[k]}
                      </option>
                    ))}
                  </SelectField>
                  <div className="flex items-end justify-end sm:col-span-2">{editable && <RemoveButton label="Remove address" onClick={() => set("addresses", v.addresses.filter((_, j) => j !== i))} />}</div>
                  <AddressField
                    label="Street"
                    required
                    className="sm:col-span-4"
                    placeholder=""
                    value={a.line1}
                    onChange={(line1) => setAddr(i, { line1 })}
                    onResolved={(r) => setAddr(i, { line1: r.line1, city: r.city, state: r.state, postalCode: r.postalCode, ...(r.line2 ? { line2: r.line2 } : {}) })}
                  />
                  <Field label="Unit" className="sm:col-span-2" value={a.line2} onChange={(e) => setAddr(i, { line2: e.target.value })} />
                  <Field label="City" required className="sm:col-span-3" value={a.city} onChange={(e) => setAddr(i, { city: e.target.value })} />
                  <Field label="State" required className="sm:col-span-1" value={a.state} onChange={(e) => setAddr(i, { state: e.target.value })} />
                  <Field label="ZIP" required className="sm:col-span-2" value={a.postalCode} onChange={(e) => setAddr(i, { postalCode: e.target.value })} />
                  <Field label="Notes (gate code, parking)" className="sm:col-span-6" maxLength={300} value={a.notes} onChange={(e) => setAddr(i, { notes: e.target.value })} />
                </div>
              ))}
              {editable && (
                <AddButton onClick={() => set("addresses", [...v.addresses, { label: "", kind: v.addresses.length ? "residence" : "home", line1: "", line2: "", city: "", state: "OK", postalCode: "", notes: "" }])}>
                  Add address
                </AddButton>
              )}
            </div>
          </Panel>
        </fieldset>

        {save.error ? <Alert>{errorMessage(save.error)}</Alert> : null}
        {notice && !dirty ? <Alert tone="ok">{notice}</Alert> : null}
        {editable && (
          <div className="sticky bottom-0 flex justify-end border-t border-line bg-paper/95 py-3 backdrop-blur">
            <Button variant="primary" type="submit" busy={save.isPending} disabled={!dirty}>
              Save details
            </Button>
          </div>
        )}
      </form>

      <OtherNumbers clientId={c.id} editable={editable} />
      <AssistantCalls clientId={c.id} />

      <Panel>
        <PanelHeader
          title="Messages and consent"
          description="Visit messages and marketing emails are on until the client opts out (Turn off here at their request, or they reply STOP to a text). Marketing texts need the client's own opt-in, when they book or sign a form."
        />
        <ul className="divide-y divide-mist">
          {c.consents.map((x) => (
            <li key={x.channel} className="flex flex-wrap items-center justify-between gap-2 px-5 py-2.5">
              <span>
                <span className="font-medium">{CONSENT_LABELS[x.channel]}</span>{" "}
                {x.granted ? <Badge tone="ok">On</Badge> : <Badge tone="off">{x.recordedAt ? "Off" : "Not opted in"}</Badge>}
                {x.assumed && <span className="ml-2 text-xs text-muted">until they opt out</span>}
                {x.recordedAt && <span className="ml-2 text-xs text-muted">{x.granted ? "since" : "opted out"} {formatDateTime(x.recordedAt)}</span>}
              </span>
              {x.granted && editable && (
                <Button variant="quiet" busy={consentOff.isPending && consentOff.variables === x.channel} onClick={() => consentOff.mutate(x.channel)}>
                  Turn off
                </Button>
              )}
            </li>
          ))}
        </ul>
        {c.consentHistory.length > 0 && (
          <details className="border-t border-mist px-5 py-3 text-sm">
            <summary className="cursor-pointer font-semibold text-muted">History</summary>
            <ul className="mt-2 flex flex-col gap-1">
              {c.consentHistory.map((h, i) => (
                <li key={i}>
                  {formatDateTime(h.recordedAt)}: {CONSENT_LABELS[h.channel]} {h.granted ? "on" : "off"} ({h.method}
                  {h.recordedBy && `, ${h.recordedBy}`}){h.wording && <span className="text-muted">: “{h.wording}”</span>}
                </li>
              ))}
            </ul>
          </details>
        )}
      </Panel>
      {c.merges.length > 0 && (
        <p className="text-sm text-muted">
          Merged into this record:{" "}
          {c.merges.map((m) => `${m.mergedName} (${formatDateTime(m.mergedAt)}${m.mergedBy ? `, by ${m.mergedBy}` : ""})`).join("; ")}.
        </p>
      )}
    </div>
  );
}

/** Numbers besides their mobile (work, a spouse's phone): calls and texts from them show as this client's. */
function OtherNumbers({ clientId, editable }: { clientId: string; editable: boolean }) {
  const { can } = useSession();
  const qc = useQueryClient();
  const list = useQuery({ queryKey: ["client-phones", clientId], queryFn: () => get<ClientPhoneView[]>(`/api/clients/${clientId}/phones`) });
  const refresh = () => Promise.all([qc.invalidateQueries({ queryKey: ["client-phones", clientId] }), qc.invalidateQueries({ queryKey: ["comm"] }), qc.invalidateQueries({ queryKey: ["calls"] }), qc.invalidateQueries({ queryKey: ["client-calls"] }), qc.invalidateQueries({ queryKey: ["client-messages"] })]);
  const [adding, setAdding] = useState<{ phone: string; label: string } | null>(null);
  const add = useMutation({
    mutationFn: () => post(`/api/clients/${clientId}/phones`, adding),
    onSuccess: async () => {
      setAdding(null);
      await refresh();
    },
  });
  const remove = useMutation({ mutationFn: (id: string) => del(`/api/clients/${clientId}/phones/${id}`), onSuccess: refresh });
  // Linked by mistake: its calls and texts go back to unknown, and it comes off the profile.
  const unlink = useMutation({
    mutationFn: async (p: ClientPhoneView) => {
      await post("/api/communications/unlink", { from: p.phone, clientId });
      if (!p.fromLink) await del(`/api/clients/${clientId}/phones/${p.id}`);
    },
    onSuccess: refresh,
  });
  const err = add.error ?? remove.error ?? unlink.error;
  return (
    <Panel>
      <PanelHeader title="Other numbers" description="Calls and texts from these show as this client's, and their calls pop up for staff." />
      <div className="flex flex-col gap-3 px-5 py-5">
        {list.data?.length ? (
          <ul className="flex flex-col divide-y divide-mist">
            {list.data.map((p) => (
              <li key={p.id} className="flex flex-wrap items-center gap-3 py-2 text-sm">
                <span className="font-semibold">{formatPhone(p.phone)}</span>
                {p.label && <span className="text-ink-soft">{p.label}</span>}
                {p.fromLink && <span className="text-xs text-muted">Saved when a call or text was linked</span>}
                {editable && (
                  <span className="ml-auto flex items-center gap-2">
                    {can("calls.view") && (
                      <Button
                        variant="quiet"
                        busy={unlink.isPending}
                        onClick={() => window.confirm(`Not ${formatPhone(p.phone)}? Its calls and texts go back to unknown.`) && unlink.mutate(p)}
                      >
                        Not theirs? Unlink
                      </Button>
                    )}
                    <RemoveButton label="Remove number" onClick={() => remove.mutate(p.id)} />
                  </span>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-muted">None.</p>
        )}
        {editable &&
          (adding ? (
            <form
              className="grid gap-3 sm:grid-cols-6"
              onSubmit={(e) => {
                e.preventDefault();
                add.mutate();
              }}
            >
              <Field label="Number" type="tel" required className="sm:col-span-2" value={adding.phone} onChange={(e) => setAdding({ ...adding, phone: e.target.value })} />
              <Field label="Label" className="sm:col-span-2" placeholder="Work, spouse's phone" maxLength={60} value={adding.label} onChange={(e) => setAdding({ ...adding, label: e.target.value })} />
              <div className="flex items-end gap-2 sm:col-span-2">
                <Button variant="secondary" type="submit" busy={add.isPending}>
                  Add
                </Button>
                <Button variant="quiet" type="button" onClick={() => setAdding(null)}>
                  Cancel
                </Button>
              </div>
            </form>
          ) : (
            <AddButton onClick={() => setAdding({ phone: "", label: "" })}>Add number</AddButton>
          ))}
        {err && <Alert>{errorMessage(err)}</Alert>}
      </div>
    </Panel>
  );
}

function NotesTab({ c, onSaved, myName }: { c: ClientDetail; onSaved: (d: ClientDetail) => void; myName: string }) {
  const [body, setBody] = useState("");
  const [correcting, setCorrecting] = useState<string | null>(null);
  const add = useMutation({
    mutationFn: () => post<ClientDetail>(`/api/clients/${c.id}/notes`, { body, correctsNoteId: correcting }),
    onSuccess: (d) => {
      onSaved(d);
      setBody("");
      setCorrecting(null);
    },
  });
  const corrected = new Set(c.notes.map((n) => n.correctsNoteId).filter(Boolean));
  return (
    <div className="flex flex-col gap-4">
      {c.status !== "merged" && (
        <Panel>
          <form className="flex flex-col gap-3 px-5 py-4" onSubmit={(e) => (e.preventDefault(), add.mutate())}>
            <TextAreaField
              label={correcting ? "Correction" : "Add a note"}
              required
              maxLength={4000}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              hint={`Signed as ${myName}. Notes can't be edited; add a correction instead.`}
            />
            {add.error && <Alert>{errorMessage(add.error)}</Alert>}
            <div className="flex justify-end gap-2">
              {correcting && (
                <Button type="button" variant="quiet" onClick={() => setCorrecting(null)}>
                  Cancel correction
                </Button>
              )}
              <Button variant="primary" type="submit" busy={add.isPending}>
                {correcting ? "Add correction" : "Add note"}
              </Button>
            </div>
          </form>
        </Panel>
      )}
      {c.notes.length === 0 ? (
        <p className="text-muted">No notes yet.</p>
      ) : (
        <ul className="flex flex-col gap-3">
          {c.notes.map((n) => (
            <li key={n.id} className={cx("rounded-[var(--radius-panel)] border border-line bg-white px-4 py-3", corrected.has(n.id) && "opacity-60")}>
              {n.correctsNoteId && <div className="mb-1 text-xs font-semibold text-wait">Correction</div>}
              {(n.kind || n.source) && (
                <div className="mb-1 flex items-center gap-2">
                  {n.kind && <span className="text-xs font-semibold uppercase tracking-wide text-wait">{n.kind === "allergy" ? "Allergies" : "Medications"}</span>}
                  <SourceBadge source={n.source} />
                </div>
              )}
              <p className="whitespace-pre-wrap">{n.body}</p>
              <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-xs text-muted">
                <span>
                  {n.authorName ?? "Unknown"} · {formatDateTime(n.createdAt)}
                  {corrected.has(n.id) && " · corrected below"}
                </span>
                {c.status !== "merged" && !n.correctsNoteId && (
                  <button className="font-semibold text-brand" onClick={() => (setCorrecting(n.id), setBody(""))}>
                    Correct
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function ClientPicker({ excludeId, onPick, withArchived = false }: { excludeId: string; onPick: (c: ClientSummary) => void; withArchived?: boolean }) {
  const tags = useTags();
  const [q, setQ] = useState("");
  const results = useClientSearch(q, false, withArchived);
  const list = (results.data ?? []).filter((c) => c.id !== excludeId);
  return (
    <div className="flex flex-col gap-2">
      <Field label="Find client" type="search" autoFocus placeholder="Name, phone, email, or birthday" value={q} onChange={(e) => setQ(e.target.value)} />
      {q.trim().length >= 2 && (results.isLoading ? <Loading /> : list.length ? <div className="max-h-72 overflow-y-auto rounded-[var(--radius-control)] border border-line"><ClientRows list={list} tags={tags.data} onPick={onPick} /></div> : <p className="text-sm text-muted">No match.</p>)}
    </div>
  );
}

function FamilyTab({ c, editable, onSaved }: { c: ClientDetail; editable: boolean; onSaved: (d: ClientDetail) => void }) {
  const [adding, setAdding] = useState(false);
  const [role, setRole] = useState<"guardian" | "ward" | "family">(c.dob && ageOn(c.dob, today()) < 18 ? "guardian" : "family");
  const link = useMutation({
    mutationFn: (otherId: string) => post<ClientDetail>(`/api/clients/${c.id}/links`, { otherId, role }),
    onSuccess: (d) => {
      onSaved(d);
      setAdding(false);
    },
  });
  const unlink = useMutation({
    mutationFn: (linkId: string) => del<ClientDetail>(`/api/clients/${c.id}/links/${linkId}`),
    onSuccess: onSaved,
  });
  const roleText = { guardian: "Parent or guardian", ward: "Their child (they're the guardian)", family: "Family" } as const;
  return (
    <div className="flex flex-col gap-4">
      <p className="text-ink-soft">Family can share a mobile or email. A minor's guardian gets their texts and forms and signs their consent.</p>
      {c.links.length > 0 && (
        <Panel>
          <ul className="divide-y divide-mist">
            {c.links.map((l) => (
              <li key={l.id} className="flex flex-wrap items-center justify-between gap-2 px-4 py-3">
                <span>
                  <Link to={`/clients/${l.other.id}`} className="font-semibold hover:text-brand">
                    {l.other.firstName} {l.other.lastName}
                  </Link>{" "}
                  <span className="text-sm text-muted">
                    {l.role === "guardian" ? "Parent or guardian" : l.role === "ward" ? "Child (this client is their guardian)" : "Family"} · {fmtDob(l.other.dob)}
                    {l.other.mobile && ` · ${formatPhone(l.other.mobile)}`}
                  </span>
                </span>
                {editable && (
                  <Button variant="quiet" busy={unlink.isPending && unlink.variables === l.id} onClick={() => unlink.mutate(l.id)}>
                    Remove link
                  </Button>
                )}
              </li>
            ))}
          </ul>
        </Panel>
      )}
      {editable &&
        (adding ? (
          <Panel>
            <div className="flex flex-col gap-3 px-5 py-4">
              <SelectField label="The person you pick is" value={role} onChange={(e) => setRole(e.target.value as typeof role)}>
                {(["guardian", "ward", "family"] as const).map((r) => (
                  <option key={r} value={r}>
                    {roleText[r]}
                  </option>
                ))}
              </SelectField>
              <ClientPicker excludeId={c.id} onPick={(o) => link.mutate(o.id)} />
              {link.error && <Alert>{errorMessage(link.error)}</Alert>}
              <div>
                <Button variant="quiet" onClick={() => setAdding(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          </Panel>
        ) : (
          <div>
            <Button onClick={() => setAdding(true)}>Link family or guardian</Button>
          </div>
        ))}
    </div>
  );
}

function ActivityTab({ clientId }: { clientId: string }) {
  const data = useQuery({ queryKey: ["client-activity", clientId], queryFn: () => get<{ entries: AuditEntry[] }>(`/api/audit?targetId=${clientId}&limit=100`) });
  if (data.isLoading) return <Loading />;
  if (data.error || !data.data) return <Alert>{errorMessage(data.error)}</Alert>;
  return (
    <Panel>
      <ul className="divide-y divide-mist">
        {data.data.entries.map((e) => (
          <li key={e.id} className="flex flex-wrap justify-between gap-2 px-4 py-2.5 text-sm">
            <span>
              <span className="font-semibold">{e.actorName ?? "System"}</span> {(AUDIT_ACTIONS as Record<string, string>)[e.action]?.toLowerCase() ?? e.action}
            </span>
            <span className="text-muted">{formatDateTime(e.occurredAt)}</span>
          </li>
        ))}
      </ul>
    </Panel>
  );
}

function TagsDialog({ c, onClose, onSaved }: { c: ClientDetail; onClose: () => void; onSaved: (d: ClientDetail) => void }) {
  const tags = useTags();
  const [ids, setIds] = useState<string[]>(c.tagIds);
  const save = useMutation({
    mutationFn: () => put<ClientDetail>(`/api/clients/${c.id}/tags`, { tagIds: ids }),
    onSuccess: (d) => {
      onSaved(d);
      onClose();
    },
  });
  return (
    <Dialog
      title="Tags"
      onClose={onClose}
      footer={
        <>
          <Button variant="quiet" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" busy={save.isPending} onClick={() => save.mutate()}>
            Save
          </Button>
        </>
      }
    >
      {save.error && <Alert>{errorMessage(save.error)}</Alert>}
      {(tags.data ?? []).length === 0 && <p className="text-sm text-muted">No tags yet. Add them under Settings → Tags.</p>}
      {(tags.data ?? []).map((t) => (
        <Checkbox key={t.id} label={t.name} checked={ids.includes(t.id)} onChange={(on) => setIds((cur) => (on ? [...cur, t.id] : cur.filter((x) => x !== t.id)))} />
      ))}
    </Dialog>
  );
}

/** Booked online as someone new who looks like an existing client: merge into theirs, or say it's someone else. */
function PossibleDuplicate({ c, onSaved }: { c: ClientDetail; onSaved: (d: ClientDetail) => void }) {
  const { can } = useSession();
  const nav = useNavigate();
  const twin = c.possibleDuplicateOf!;
  const merge = useMutation({
    mutationFn: () => post<ClientDetail>(`/api/clients/${twin.id}/merge`, { mergeId: c.id }),
    onSuccess: () => nav(`/clients/${twin.id}`),
  });
  const dismiss = useMutation({ mutationFn: () => post<ClientDetail>(`/api/clients/${c.id}/not-duplicate`, {}), onSuccess: onSaved });
  return (
    <div className="mb-4 rounded-[var(--radius-control)] border border-wait/40 bg-wait-wash px-4 py-3 text-sm" role="note">
      <p>
        <span className="font-semibold">Possibly the same person as </span>
        <Link to={`/clients/${twin.id}`} className="font-semibold underline">
          {twin.name}
        </Link>
        {twin.dob && ` (born ${twin.dob})`}. They booked online with details that only partly match that record, so this one was made new. Check, then merge them or mark them as different people.
      </p>
      <div className="mt-2 flex flex-wrap gap-2">
        {can("clients.merge") && (
          <Button busy={merge.isPending} onClick={() => merge.mutate()}>
            Merge into {twin.name}
          </Button>
        )}
        {can("clients.edit") && (
          <Button variant="quiet" busy={dismiss.isPending} onClick={() => dismiss.mutate()}>
            Not the same person
          </Button>
        )}
      </div>
      {(merge.error || dismiss.error) && <p className="mt-2 text-danger">{errorMessage(merge.error ?? dismiss.error)}</p>}
    </div>
  );
}

function MergeDialog({ c, onClose, onSaved }: { c: ClientDetail; onClose: () => void; onSaved: (d: ClientDetail) => void }) {
  const [other, setOther] = useState<ClientSummary | null>(null);
  const [leftBehind, setLeftBehind] = useState<string[] | null>(null);
  const merge = useMutation({
    mutationFn: () => post<ClientDetail & { mergeNotes: string[] }>(`/api/clients/${c.id}/merge`, { mergeId: other!.id }),
    onSuccess: ({ mergeNotes, ...d }) => {
      onSaved(d);
      if (mergeNotes.length) setLeftBehind(mergeNotes);
      else onClose();
    },
  });
  if (leftBehind)
    return (
      <Dialog title="Merged" onClose={onClose} footer={<Button onClick={onClose}>Done</Button>}>
        <Alert tone="info">
          Merged. One thing to sort out:
          <ul className="mt-2 list-disc pl-5">
            {leftBehind.map((n) => (
              <li key={n}>{n}</li>
            ))}
          </ul>
        </Alert>
      </Dialog>
    );
  return (
    <Dialog
      title="Merge a duplicate"
      onClose={onClose}
      wide
      footer={
        <>
          <Button variant="quiet" onClick={onClose}>
            Cancel
          </Button>
          {other && (
            <Button variant="danger" busy={merge.isPending} onClick={() => merge.mutate()}>
              Merge into {c.firstName} {c.lastName}
            </Button>
          )}
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <p className="text-sm text-ink-soft">
          Pick the duplicate. <strong>This record ({displayName(c)}, {fmtDob(c.dob)}) is kept</strong>; any blank details are filled from the duplicate, and everything on it moves here: visits, charts, forms, good faith exams, payments, memberships and packages, messages and calls, school notes, addresses, tags, notes, consent history, other phone numbers, and family links. The duplicate is kept as "merged" and the merge is logged.
        </p>
        {other ? (
          <div className="rounded-[var(--radius-control)] border border-line px-4 py-3">
            <div className="font-semibold">{displayName(other)}</div>
            <div className="text-sm text-muted">
              {fmtDob(other.dob)}
              {other.mobile && ` · ${formatPhone(other.mobile)}`}
              {other.email && ` · ${other.email}`}
            </div>
            {other.dob !== c.dob && <p className="mt-2 text-sm font-semibold text-wait">Different date of birth. Make sure it's really the same person.</p>}
            <button className="mt-2 text-sm font-semibold text-brand" onClick={() => setOther(null)}>
              Pick someone else
            </button>
          </div>
        ) : (
          <ClientPicker excludeId={c.id} onPick={setOther} withArchived />
        )}
        {merge.error && <Alert>{errorMessage(merge.error)}</Alert>}
      </div>
    </Dialog>
  );
}

/** Staff names come from the calendar feed, which needs appointments.manage. */
function useStaffNames() {
  const { can } = useSession();
  const day = todayIn();
  const cal = useCalendar(day, day, can("appointments.manage"));
  const staff = cal.data?.staff ?? [];
  return { staff, name: (id: string | null) => staff.find((s) => s.id === id)?.name ?? "" };
}

function BookFor({ c, onClose }: { c: ClientDetail; onClose: () => void }) {
  const { staff } = useStaffNames();
  return <BookingDialog prefill={{ date: todayIn(), client: c }} staff={staff} onClose={onClose} onBooked={onClose} />;
}

/** Forms and charts brought in from the old system (Boulevard), shown with the clinic's own. */
const useImported = (clientId: string) => useQuery({ queryKey: ["client-imported", clientId], queryFn: () => get<ImportedRecordSummary[]>(`/api/clients/${clientId}/imported`) });

function VisitsTab({ clientId }: { clientId: string }) {
  const { can } = useSession();
  const { name } = useStaffNames();
  const openVisit = useNavigate();
  const setOpenId = (id: string) => openVisit(`/visits/${id}`);
  const list = useQuery({ queryKey: ["client-appointments", clientId], queryFn: () => get<AppointmentView[]>(`/api/clients/${clientId}/appointments`) });
  const before = useQuery({ queryKey: ["past-visits", clientId], queryFn: () => get<PastVisitView[]>(`/api/clients/${clientId}/past-visits`) });
  const imported = useImported(clientId);
  if (list.isLoading) return <Loading />;
  if (list.error) return <Alert>{errorMessage(list.error)}</Alert>;
  const rows = list.data ?? [];
  const old = before.data ?? [];
  if (!rows.length && !old.length) return <p className="text-sm text-muted">No visits yet.</p>;
  const now = todayIn();
  // Finished today (completed or no-show) counts as past.
  const isPast = (a: AppointmentView) => a.start.slice(0, 10) < now || a.status === "completed" || a.status === "no_show";
  const upcoming = rows.filter((a) => !isPast(a)).reverse();
  // Past visits, with those from the old system (tagged) in date order among them.
  const past = [...rows.filter(isPast).map((a) => ({ at: a.start, a, v: null as PastVisitView | null })), ...old.map((v) => ({ at: `${v.date}T${v.start ?? "00:00"}`, a: null, v }))].sort((x, y) => y.at.localeCompare(x.at));
  const chartFor = (v: PastVisitView) => imported.data?.find((r) => r.kind === "chart" && r.visitRef === v.ref);
  const row = (a: AppointmentView) => (
    <li key={a.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-mist py-2.5 last:border-0">
      <span className="w-40 text-sm tabular-nums">
        {new Date(`${a.start.slice(0, 10)}T12:00:00Z`).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" })} · {time12(a.start)}
      </span>
      <span className="min-w-0 flex-1 text-sm font-semibold">
        {a.serviceName}
        {a.addOns.length > 0 && <span className="font-normal text-ink-soft"> + {a.addOns.map((x) => x.name).join(", ")}</span>}
        {a.staffId && name(a.staffId) && <span className="font-normal text-muted"> · {name(a.staffId)}</span>}
      </span>
      {a.bookingSource === "boulevard" && <SourceBadge source="boulevard" />}
      <FormsBadge a={a} compact />
      {a.status !== "cancelled" && a.status !== "completed" && <GfeBadge g={a.gfe} compact />}
      {a.holdUntil ? <HoldBadge until={a.holdUntil} /> : <Badge tone={STATUS_TONE[a.status]}>{STATUS_LABELS[a.status]}</Badge>}
      {a.boulevardChart && (
        <Link to={`/records/${a.boulevardChart.id}`} className="text-sm font-semibold text-brand hover:text-brand-deep">
          Chart
        </Link>
      )}
      {a.status !== "cancelled" && (!a.finishedInBoulevard || a.boulevardReceipt) && <span className="text-sm tabular-nums text-ink-soft">{formatCents(a.boulevardReceipt ? a.boulevardReceipt.totalCents : a.priceCents)}</span>}
      {can("appointments.manage") && (
        <button className="text-sm font-semibold text-brand hover:text-brand-deep" onClick={() => setOpenId(a.id)}>
          Open
        </button>
      )}
    </li>
  );
  return (
    <div className="flex flex-col gap-4">
      {upcoming.length > 0 && (
        <Panel>
          <PanelHeader title="Upcoming" />
          <ul className="px-5 pb-3">{upcoming.map(row)}</ul>
        </Panel>
      )}
      {past.length > 0 && (
        <Panel>
          <PanelHeader title="Past" />
          <ul className="px-5 pb-3">{past.map((p) => (p.a ? row(p.a) : <PastVisitRow key={p.v!.id} v={p.v!} chartId={chartFor(p.v!)?.id} />))}</ul>
        </Panel>
      )}
    </div>
  );
}

const VIA_LABEL = { link: "Forms link", kiosk: "iPad at check-in", staff: "Staff" } as const;

function FormsTab({ clientId, minor }: { clientId: string; minor: boolean }) {
  const list = useQuery({ queryKey: ["client-forms", clientId], queryFn: () => get<SubmissionView[]>(`/api/clients/${clientId}/forms`) });
  const health = useQuery({ queryKey: ["client-health", clientId], queryFn: () => get<HealthSummary>(`/api/clients/${clientId}/health`) });
  const [openId, setOpenId] = useState<string | null>(null);
  const [showVoided, setShowVoided] = useState(false);
  const [voiding, setVoiding] = useState<SubmissionView | null>(null);
  const { can } = useSession();
  const imported = useImported(clientId);
  const importedForms = (imported.data ?? []).filter((r) => r.kind !== "chart" && (showVoided || !r.voided));
  if (list.isLoading) return <Loading />;
  if (list.error) return <Alert>{errorMessage(list.error)}</Alert>;
  const all = list.data ?? [];
  const voidedCount = all.filter((s) => s.voided).length;
  const rows = showVoided ? all : all.filter((s) => !s.voided);
  return (
    <div className="flex flex-col gap-4">
      {health.data && (health.data.intakeSignedAt || health.data.consentSignedAt) && (
        <Panel>
          <PanelHeader title="Health summary" description={health.data.intakeSource ? `From the intake signed in ${SOURCE_LABELS[health.data.intakeSource] ?? health.data.intakeSource} and the latest per-visit consent.` : "From the latest intake and per-visit consent."} />
          <div className="px-5 pb-5">
            <HealthBlock h={health.data} />
          </div>
        </Panel>
      )}
      <Panel>
        <PanelHeader
          title="Signed forms"
          description="Each signed copy keeps the exact wording that was signed. Forms for upcoming visits are requested from the appointment."
          action={
            voidedCount > 0 ? (
              <Checkbox label={`Show voided (${voidedCount})`} checked={showVoided} onChange={setShowVoided} />
            ) : undefined
          }
        />
        {rows.length || importedForms.length ? (
          <ul className="px-5 pb-3">
            {[...rows.map((x) => ({ at: x.signedAt, s: x, r: null as ImportedRecordSummary | null })), ...importedForms.map((r) => ({ at: r.signedAt ?? "", s: null, r }))]
              .sort((x, y) => y.at.localeCompare(x.at))
              .map(({ s, r }) =>
                r ? (
                  <li key={r.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-mist py-2.5 last:border-0">
                    <span className="w-44 text-sm">{r.signedAt ? formatDateTime(r.signedAt) : ""}</span>
                    <span className={cx("min-w-0 flex-1 text-sm font-semibold", r.voided && "text-muted line-through")}>
                      {r.name} <SourceBadge source={r.source} />
                    </span>
                    {r.signedBy && <span className="text-sm text-ink-soft">{r.signedBy}</span>}
                    <Link to={`/records/${r.id}`} className="text-sm font-semibold text-brand hover:text-brand-deep">
                      View
                    </Link>
                  </li>
                ) : (
                  <li key={s!.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-mist py-2.5 last:border-0">
                    <span className="w-44 text-sm">{formatDateTime(s!.signedAt)}</span>
                    <span className={cx("min-w-0 flex-1 text-sm font-semibold", s!.voided && "text-muted line-through")}>
                      {s!.name} <span className="font-normal text-muted">v{s!.version}</span>
                    </span>
                    {s!.voided && (
                      <span className="w-full text-xs text-muted">
                        Voided {formatDateTime(s!.voided.at)} by {s!.voided.by}: {s!.voided.reason}
                      </span>
                    )}
                    <span className="text-sm text-ink-soft">
                      {s!.signedName}
                      {s!.signer === "guardian" && " (guardian)"} · {VIA_LABEL[s!.via]}
                    </span>
                    <button className="text-sm font-semibold text-brand hover:text-brand-deep" onClick={() => setOpenId(s!.id)}>
                      View
                    </button>
                    {can("forms.void") && !s!.voided && (
                      <button className="text-sm font-semibold text-muted hover:text-danger" onClick={() => setVoiding(s!)}>
                        Void
                      </button>
                    )}
                  </li>
                ),
              )}
          </ul>
        ) : (
          <p className="px-5 pb-5 text-sm text-muted">No signed forms yet.</p>
        )}
      </Panel>
      {openId && <SubmissionDialog id={openId} minor={minor} onClose={() => setOpenId(null)} />}
      {voiding && <VoidFormDialog s={voiding} clientId={clientId} onClose={() => setVoiding(null)} />}
    </div>
  );
}

/** Void a signed form (Super Admin): it stays on record, but no longer counts. */
function VoidFormDialog({ s, clientId, onClose }: { s: SubmissionView; clientId: string; onClose: () => void }) {
  const qc = useQueryClient();
  const [reason, setReason] = useState("");
  const save = useMutation({
    mutationFn: () => post(`/api/form-submissions/${s.id}/void`, { reason }),
    onSuccess: async () => {
      await Promise.all([qc.invalidateQueries({ queryKey: ["client-forms", clientId] }), qc.invalidateQueries({ queryKey: ["client-health", clientId] })]);
      onClose();
    },
  });
  return (
    <Dialog
      title={`Void ${s.name}?`}
      onClose={onClose}
      footer={
        <>
          <Button variant="quiet" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="danger" busy={save.isPending} disabled={!reason.trim()} onClick={() => save.mutate()}>
            Void form
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <p className="text-sm text-ink-soft">
          Signed {formatDateTime(s.signedAt)} by {s.signedName}. The signed copy stays on record, marked voided; it no longer counts, so the client signs again if a visit needs it. This can't be undone.
        </p>
        <Field label="Reason" placeholder="e.g. Test entry, wrong client" value={reason} onChange={(e) => setReason(e.target.value)} />
        {save.error && <Alert>{errorMessage(save.error)}</Alert>}
      </div>
    </Dialog>
  );
}

function SubmissionDialog({ id, minor, onClose }: { id: string; minor: boolean; onClose: () => void }) {
  const s = useQuery({ queryKey: ["form-submission", id], queryFn: () => get<SubmissionDetail>(`/api/form-submissions/${id}`) });
  const d = s.data;
  return (
    <Dialog
      title={d ? d.name : "Signed form"}
      onClose={onClose}
      wide
      footer={
        <Button variant="quiet" onClick={() => window.print()}>
          Print
        </Button>
      }
    >
      {s.error ? (
        <Alert>{errorMessage(s.error)}</Alert>
      ) : !d ? (
        <Loading />
      ) : (
        <div className="flex flex-col gap-4 print:text-black">
          <header>
            <h2 className="font-display text-xl">{d.definition.title}</h2>
            {d.definition.subtitle && <p className="text-sm text-ink-soft">{d.definition.subtitle}</p>}
          </header>
          <FormBody def={d.definition} minor={d.signer === "guardian" || (minor && !!d.guardian)} answers={d.answers} details={d.details} guardian={d.guardian} readOnly />
          <section className="rounded-[var(--radius-control)] bg-mist/60 p-3 text-sm">
            {d.signature && <SignatureImage src={d.signature} />}
            <p>
              Signed by <span className="font-semibold">{d.signedName}</span>
              {d.signer === "guardian" && " (parent or guardian)"} on {formatDateTime(d.signedAt)} via {VIA_LABEL[d.via].toLowerCase()}. Form version {d.version}.
            </p>
            <p className="mt-1 break-all text-xs text-muted">
              {d.ip && `IP ${d.ip} · `}Fingerprint {d.contentHash}
            </p>
          </section>
        </div>
      )}
    </Dialog>
  );
}

function ChartsTab({ clientId }: { clientId: string }) {
  const list = useQuery({ queryKey: ["client-charts", clientId], queryFn: () => get<ChartSummary[]>(`/api/clients/${clientId}/charts`) });
  const imported = useImported(clientId);
  if (list.isLoading) return <Loading />;
  if (list.error) return <Alert>{errorMessage(list.error)}</Alert>;
  const rows = list.data ?? [];
  const old = (imported.data ?? []).filter((r) => r.kind === "chart");
  return (
    <Panel>
      <PanelHeader title="Charts" description="Nurse charts for each visit. Opening one is logged." />
      {rows.length || old.length ? (
        <ul className="px-5 pb-3">
          {rows.map((r) => (
            <li key={r.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-mist py-2.5 last:border-0">
              <span className="w-28 text-sm tabular-nums">{r.date}</span>
              <span className="min-w-0 flex-1 text-sm font-semibold">
                {r.isUpdate ? "Updated " : ""}
                {r.name} <span className="font-normal text-ink-soft">· {r.serviceName}</span>
              </span>
              {r.replacedAt ? (
                <Badge tone="off">Replaced by the updated chart, {formatDateTime24(r.replacedAt)}</Badge>
              ) : r.status === "signed" ? (
                <Badge tone="ok">Signed{r.signedBy ? ` · ${r.signedBy}` : ""}</Badge>
              ) : (
                <Badge tone="wait">Draft</Badge>
              )}
              {r.review.status === "waiting" && !r.replacedAt && <Badge tone="wait">Waiting for MD review</Badge>}
              {r.review.status === "reviewed" && <Badge tone="ok">MD reviewed</Badge>}
              <Link to={`/charts/${r.id}`} className="text-sm font-semibold text-brand hover:text-brand-deep">
                Open
              </Link>
            </li>
          ))}
          {/* The old system's charts: all from before Vaulted, so after these. */}
          {old.map((o) => (
            <li key={o.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-mist py-2.5 last:border-0">
              <span className="w-28 text-sm tabular-nums">{o.signedAt?.slice(0, 10)}</span>
              <span className="min-w-0 flex-1 text-sm font-semibold">
                {o.name} <SourceBadge source={o.source} />
              </span>
              <Badge tone="ok">Signed{o.signedBy ? ` · ${o.signedBy}` : ""}</Badge>
              {o.review.status === "waiting" && <Badge tone="wait">Waiting for MD review</Badge>}
              {o.review.status === "reviewed" && <Badge tone="ok">MD reviewed</Badge>}
              <Link to={`/records/${o.id}`} className="text-sm font-semibold text-brand hover:text-brand-deep">
                Open
              </Link>
            </li>
          ))}
        </ul>
      ) : (
        <p className="px-5 pb-5 text-sm text-muted">No charts yet.</p>
      )}
    </Panel>
  );
}

function PaymentsTab({ clientId }: { clientId: string }) {
  const { can } = useSession();
  const pays = useQuery({ queryKey: ["client-payments", clientId], queryFn: () => get<PaymentView[]>(`/api/clients/${clientId}/payments`) });
  const cards = useQuery({ queryKey: ["client-cards", clientId], queryFn: () => get<SavedCardView[]>(`/api/clients/${clientId}/cards`) });
  const remove = useMutation({ mutationFn: (id: string) => del(`/api/cards/${id}`), onSuccess: () => void cards.refetch() });
  if (pays.isLoading || cards.isLoading) return <Loading />;
  return (
    <div className="flex flex-col gap-4">
      <AccountCredit clientId={clientId} />
      <Panel>
        <PanelHeader title="Cards on file" description="Saved with Stripe when booking or from a link; only the brand and last 4 digits are kept here." />
        {cards.data?.length ? (
          <ul className="px-5 pb-3">
            {cards.data.map((c) => (
              <li key={c.id} className="flex items-center gap-3 border-b border-mist py-2.5 text-sm last:border-0">
                <span className="flex-1 font-semibold">
                  {c.brand.toUpperCase()} •••• {c.last4}
                  {c.expMonth && <span className="font-normal text-muted"> · expires {c.expMonth}/{c.expYear}</span>}
                </span>
                <span className="text-muted">Saved {formatDateTime(c.savedAt)}</span>
                {can("clients.edit") && (
                  <button className="text-sm text-danger hover:underline" onClick={() => confirm("Remove this card?") && remove.mutate(c.id)}>
                    Remove
                  </button>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <p className="px-5 pb-5 text-sm text-muted">No cards on file.</p>
        )}
        {can("appointments.manage") && (
          <div className="px-5 pb-5">
            <CardLinkButtons clientId={clientId} />
          </div>
        )}
      </Panel>
      <Panel>
        <PanelHeader title="Payments" />
        {pays.data?.length ? (
          <ul className="px-5 pb-3">
            {pays.data.map((p) => (
              <li key={p.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-mist py-2.5 text-sm last:border-0">
                <span className="w-40 text-muted">{formatDateTime(p.at)}</span>
                <span className="min-w-0 flex-1">
                  <span className="font-semibold">{p.kind === "refund" ? "Refund" : p.kind === "deposit" ? "Deposit" : p.kind === "fee" ? `Fee (${p.reason})` : p.kind === "plan" ? (p.reason ?? "Membership or package") : "Payment"}</span> · {PAYMENT_METHOD_LABELS[p.method]}
                  {p.cardLast4 && ` •••• ${p.cardLast4}`}
                  {p.visit && <span className="text-muted"> · {p.visit.serviceName}, {p.visit.date}</span>}
                  {p.failureMessage && <span className="block text-xs text-danger">{p.failureMessage}</span>}
                </span>
                <Badge tone={p.status === "succeeded" ? "ok" : p.status === "failed" ? "off" : "wait"}>{p.status === "succeeded" ? "Paid" : p.status === "authorized" ? "Held" : p.status}</Badge>
                <span className="w-20 text-right font-semibold tabular-nums">
                  {p.kind === "refund" ? "−" : ""}
                  {formatCents(p.amountCents)}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="px-5 pb-5 text-sm text-muted">No payments yet.</p>
        )}
      </Panel>
    </div>
  );
}

export function MessagesTab({ clientId }: { clientId: string }) {
  const qc = useQueryClient();
  const { can } = useSession();
  const list = useQuery({ queryKey: ["client-messages", clientId], queryFn: () => get<MessageView[]>(`/api/clients/${clientId}/messages`), refetchInterval: 30_000 });
  const [channel, setChannel] = useState<"sms" | "email">("sms");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [to, setTo] = useState("");
  const numbers = useTextNumbers(clientId);
  // Their latest message was an email: answer by email, "Re: …" its subject.
  const lastIn = list.data?.find((m) => m.direction === "in");
  const answered = useRef<string | null>(null);
  useEffect(() => {
    if (!lastIn || answered.current === lastIn.id) return;
    answered.current = lastIn.id;
    if (lastIn.channel === "email") {
      setChannel("email");
      setSubject(lastIn.subject ? (/^re:/i.test(lastIn.subject) ? lastIn.subject : `Re: ${lastIn.subject}`) : "");
    }
  }, [lastIn]);
  const send = useMutation({
    mutationFn: () => post(`/api/clients/${clientId}/messages`, { channel, subject, body, to: channel === "sms" ? to || null : null }),
    // A reply answers the conversation on the Communication page too.
    onSuccess: () => (setBody(""), setSubject(""), void list.refetch(), void qc.invalidateQueries({ queryKey: ["comm"] })),
  });
  if (list.isLoading) return <Loading />;
  // Newest first, with the write box above them.
  const rows = list.data ?? [];
  return (
    <div className="flex flex-col gap-4">
      <ClientCalls clientId={clientId} />
      <Panel>
        <PanelHeader title="Communications" description="Every text and email to and from this client, newest first, including automatic reminders. Their replies, by text or email, show up here." />
        {can("appointments.manage") && (
          <Compose
            clientId={clientId}
            channel={channel}
            setChannel={setChannel}
            subject={subject}
            setSubject={setSubject}
            body={body}
            setBody={setBody}
            to={to}
            setTo={setTo}
            numbers={numbers.data}
            onSend={() => send.mutate()}
            sending={send.isPending}
            error={send.error ? errorMessage(send.error) : null}
          />
        )}
        <ul className="flex flex-col gap-3 px-5 pb-5">
          {pairTextAndEmail(rows).map((x) => {
            const versions = isPair(x) ? x.pair : [x];
            return <MessageEntry key={versions[0]!.id} versions={versions} />;
          })}
          {!rows.length && <li className="text-sm text-muted">Nothing yet.</li>}
        </ul>
      </Panel>
    </div>
  );
}

/** Writing to the client: Text or Email (pink or blue, like the message tabs), who it goes to, the words, and Send. */
function Compose(p: {
  clientId: string;
  channel: "sms" | "email";
  setChannel: (c: "sms" | "email") => void;
  subject: string;
  setSubject: (s: string) => void;
  body: string;
  setBody: (s: string) => void;
  to: string;
  setTo: (s: string) => void;
  numbers: TextNumber[] | undefined;
  onSend: () => void;
  sending: boolean;
  error: string | null;
}) {
  const client = useQuery({ queryKey: ["client", p.clientId], queryFn: () => get<ClientDetail>(`/api/clients/${p.clientId}`), staleTime: 60_000 });
  const c = client.data;
  const first = c ? c.preferredName || c.firstName : "them";
  const email = p.channel === "email";
  const mobile = p.numbers?.find((n) => n.mobile);
  const segments = p.body.length > 160 ? Math.ceil(p.body.length / 153) : 1;
  const ready = p.body.trim().length > 0 && (!email || !!c?.email);
  const send = () => ready && !p.sending && p.onSend();
  return (
    <form
      className="mx-5 mb-4 rounded-[var(--radius-panel)] border border-line bg-white shadow-sm"
      onSubmit={(e) => {
        e.preventDefault();
        send();
      }}
    >
      <div className="flex flex-wrap items-center gap-3 border-b border-mist px-3 py-2.5">
        <div className="inline-flex rounded-full bg-mist p-1" role="radiogroup" aria-label="Send by">
          {(["sms", "email"] as const).map((k) => (
            <button
              key={k}
              type="button"
              role="radio"
              aria-checked={p.channel === k}
              onClick={() => p.setChannel(k)}
              className={cx("flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-sm font-semibold", p.channel === k ? cx("bg-white shadow-sm", k === "email" ? "text-[#1d4ed8]" : "text-brand") : "text-muted hover:text-ink-soft")}
            >
              <svg viewBox="0 0 24 24" className="size-4" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                {k === "email" ? <path d="M3 6h18v12H3zM3 7l9 6 9-6" /> : <path d="M4 5h16v11H8l-4 4V5z" />}
              </svg>
              {k === "email" ? "Email" : "Text"}
            </button>
          ))}
        </div>
        <div className="flex min-w-0 items-center gap-2 text-sm text-muted">
          to
          {email ? (
            <span className="truncate font-semibold text-ink">{c?.email || "no email on file"}</span>
          ) : p.numbers && p.numbers.length > 1 ? (
            <select aria-label="To" className="rounded-[var(--radius-control)] border border-line bg-paper px-2 py-1 font-semibold text-ink" value={p.to} onChange={(e) => p.setTo(e.target.value)}>
              {p.numbers.map((n) => (
                <option key={n.phone} value={n.mobile ? "" : n.phone}>
                  {n.mobile ? "Mobile" : n.label || "Other number"} {formatPhone(n.phone)}
                </option>
              ))}
            </select>
          ) : (
            <span className="font-semibold text-ink">{mobile ? `Mobile ${formatPhone(mobile.phone)}` : "no mobile on file"}</span>
          )}
        </div>
      </div>
      {email && (
        <label className="flex items-center gap-2 border-b border-mist px-3.5 py-2 text-[15px]">
          <span className="text-muted">Subject</span>
          <input className="min-w-0 flex-1 bg-transparent outline-none" value={p.subject} onChange={(e) => p.setSubject(e.target.value)} />
        </label>
      )}
      <textarea
        aria-label="Message"
        rows={3}
        className="block w-full resize-y bg-transparent px-3.5 py-3 text-[15px] outline-none placeholder:text-muted/70"
        placeholder={`Write ${email ? "an email" : "a text"} to ${first}…`}
        value={p.body}
        onChange={(e) => p.setBody(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            send();
          }
        }}
      />
      {p.error && (
        <div className="px-3.5 pb-2">
          <Alert>{p.error}</Alert>
        </div>
      )}
      <div className="flex flex-wrap items-center justify-between gap-2 px-3 pb-3">
        <span className="text-xs text-muted">
          {email ? "From the clinic's email, with its logo and footer" : "From the clinic's number"} · ⌘/Ctrl + Enter to send
          {!email && ` · ${p.body.length}/160${segments > 1 ? ` (sends as ${segments} texts)` : ""}`}
        </span>
        <button type="submit" disabled={!ready || p.sending} className={cx("flex items-center gap-1.5 rounded-[var(--radius-control)] px-4 py-2 text-sm font-bold text-white disabled:opacity-50", email ? "bg-[#1d4ed8]" : "bg-brand")}>
          {p.sending ? "Sending…" : email ? "Send email" : "Send text"}
          <svg viewBox="0 0 24 24" className="size-4" fill="currentColor" aria-hidden>
            <path d="M3 20l18-8L3 4v6l12 2-12 2z" />
          </svg>
        </button>
      </div>
    </form>
  );
}

/**
 * A message's words. An incoming email with a designed version shows it as sent (in a frame that
 * runs nothing; pictures from elsewhere only on "Show images"), with the plain text a tap away.
 */
const whoLabel = (m: MessageView) => (m.direction === "in" ? "Reply" : (m.label ?? (m.sentBy ? `by ${m.sentBy}` : "Sent")));

function DeliveryNote({ m }: { m: MessageView }) {
  if (m.status === "failed") return <p className="mt-1 text-danger">Failed{m.error ? `: ${m.error}` : ""}</p>;
  if (m.status === "skipped") return <p className="mt-1 text-wait">Not sent{m.error ? `: ${m.error}` : ""}</p>;
  return null;
}

/** An email's text, folded to a few lines until opened (long confirmations shouldn't swamp the thread). */
function EmailText({ m }: { m: MessageView }) {
  const [open, setOpen] = useState(false);
  // Incoming designed emails have their own viewer.
  if (m.html) return <MessageBody m={m} />;
  return (
    <>
      <p className={cx("whitespace-pre-wrap", !open && "line-clamp-3")}>{m.body}</p>
      {m.body.split("\n").length > 3 || m.body.length > 240 ? (
        <button type="button" className="mt-1 text-xs font-semibold text-brand" onClick={() => setOpen(!open)}>
          {open ? "Show less" : "Show full email"}
        </button>
      ) : null}
    </>
  );
}

/**
 * One message, or the same one sent by text and by email at once: folder tabs ("Text to …", "Email to
 * …"; a message sent one way has one tab) over the message itself, a text as a chat bubble, an email
 * with its subject. `head` is what goes above the tabs (who, when).
 */
export function MessageTabs({ versions, head }: { versions: MessageView[]; head: ReactNode }) {
  const [tab, setTab] = useState(0);
  const m = versions[tab] ?? versions[0]!;
  const inbound = m.direction === "in";
  return (
    <div className={cx("rounded-[var(--radius-panel)] border px-3 pt-2.5 pb-3 text-sm", inbound ? "border-line bg-paper" : "border-brand/20 bg-brand/5")}>
      <div className="mb-2 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-xs text-muted">{head}</div>
      <div className="relative z-[1] flex flex-wrap gap-1 pl-2.5" role="tablist">
        {versions.map((v, i) => {
          const email = v.channel === "email";
          const on = i === tab;
          const who = email ? v.to : formatPhone(normalizePhone(v.to) ?? v.to);
          return (
            <button
              key={v.id}
              type="button"
              role="tab"
              aria-selected={on}
              onClick={() => setTab(i)}
              className={cx(
                "flex items-center gap-1.5 rounded-t-[10px] border border-b-0 px-3 py-1.5 text-[13px]",
                on ? cx("-mb-px border-t-[3px] bg-white pb-2 text-ink", email ? "border-x-line border-t-[#1d4ed8]" : "border-x-line border-t-brand") : "border-line bg-mist/70 text-muted hover:text-ink-soft",
              )}
            >
              <svg viewBox="0 0 24 24" className={cx("size-3.5", on && (email ? "text-[#1d4ed8]" : "text-brand"))} fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                {email ? <path d="M3 6h18v12H3zM3 7l9 6 9-6" /> : <path d="M4 5h16v11H8l-4 4V5z" />}
              </svg>
              <span className={cx(on && "font-semibold", on && (email ? "text-[#1d4ed8]" : "text-brand"))}>{email ? "Email" : "Text"}</span>
              {who && <span>{`${v.direction === "in" ? "from" : "to"} ${who}`}</span>}
            </button>
          );
        })}
      </div>
      <div className="rounded-[12px] rounded-tl-none border border-line bg-white px-3 py-2.5">
        {m.channel === "email" ? (
          <>
            {m.subject && <div className="font-semibold">{m.subject}</div>}
            <EmailText m={m} />
          </>
        ) : (
          <div className={cx("inline-block max-w-full rounded-2xl px-3 py-2", inbound ? "rounded-bl-sm bg-mist" : "rounded-br-sm bg-brand/10")}>
            <MessageBody m={m} />
          </div>
        )}
        <MessageFiles m={m} />
        <div className="text-xs">
          <DeliveryNote m={m} />
        </div>
      </div>
    </div>
  );
}

/** A message (or a text-and-email pair) on the client's Communications tab. */
function MessageEntry({ versions }: { versions: MessageView[] }) {
  const m = versions[0]!;
  return (
    <li>
      <MessageTabs
        versions={versions}
        head={
          <>
            <DirectionTag direction={m.direction} />
            <span className="font-semibold text-ink-soft">{whoLabel(m)}</span>
            {versions.length > 1 && <span>· sent by text and email</span>}
            <span>· {formatDateTime(m.at)}</span>
          </>
        }
      />
    </li>
  );
}

export function MessageBody({ m }: { m: Pick<MessageView, "id" | "body"> & { html?: MessageView["html"] } }) {
  const [plain, setPlain] = useState(false);
  const [images, setImages] = useState(false);
  const [quoted, setQuoted] = useState(false);
  const [height, setHeight] = useState(80);
  const link = "text-xs font-semibold text-brand hover:underline";
  if (!m.html || plain)
    return (
      <>
        <div className="whitespace-pre-wrap [overflow-wrap:anywhere]">{m.body}</div>
        {m.html && (
          <button type="button" className={cx(link, "mt-1")} onClick={() => setPlain(false)}>
            Show as designed
          </button>
        )}
      </>
    );
  return (
    <>
      <iframe
        title="Email"
        src={`/api/messages/${m.id}/html?images=${images ? 1 : 0}&quoted=${quoted ? 1 : 0}`}
        sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
        className="block w-full min-w-[260px] border-0"
        style={{ height }}
        // Sized to fit what's in it (it can't run script to say so itself).
        onLoad={(e) => {
          const d = e.currentTarget.contentDocument;
          if (d) setHeight(Math.min(d.documentElement.scrollHeight + 4, 1600));
        }}
      />
      <div className="mt-1 flex flex-wrap gap-x-3">
        {m.html.remoteImages && !images && (
          <button type="button" className={link} onClick={() => setImages(true)}>
            Show images
          </button>
        )}
        {m.html.quoted && (
          <button type="button" className={link} onClick={() => setQuoted(!quoted)}>
            {quoted ? "Hide quoted text" : "Show quoted text"}
          </button>
        )}
        <button type="button" className={link} onClick={() => setPlain(true)}>
          Plain text
        </button>
      </div>
    </>
  );
}

/** Files that came with an email (each opens in a new tab). */
export function MessageFiles({ m }: { m: Pick<MessageView, "id" | "attachments"> }) {
  if (!m.attachments.length) return null;
  return (
    <ul className="mt-1.5 flex flex-wrap gap-2">
      {m.attachments.map((a, i) => (
        <li key={i}>
          <a href={`/api/messages/${m.id}/attachments/${i}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded border border-line bg-white px-2 py-0.5 text-xs font-semibold text-brand hover:underline">
            📎 {a.name} <span className="font-normal text-muted">({Math.max(1, Math.round(a.size / 1024))} KB)</span>
          </a>
        </li>
      ))}
    </ul>
  );
}

/** Fees owed (a no-show or late cancel that couldn't be charged): shown until added to a bill at checkout, or waived. */
function FeesOwedBanner({ clientId }: { clientId: string }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["fees-owed", clientId], queryFn: () => get<FeeOwed[]>(`/api/clients/${clientId}/fees-owed`) });
  const waive = useMutation({ mutationFn: (id: string) => post(`/api/fees/${id}/waive`, {}), onSuccess: () => void qc.invalidateQueries({ queryKey: ["fees-owed", clientId] }) });
  if (!q.data?.length) return null;
  return (
    <Alert tone="info">
      <div className="flex flex-col gap-1">
        {q.data.map((f) => (
          <div key={f.paymentId} className="flex flex-wrap items-center gap-2">
            <span className="font-semibold">
              Fee owed: {formatCents(f.amountCents)} · {f.reason}, {new Date(`${f.date}T12:00:00Z`).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" })}
            </span>
            <span className="text-sm text-ink-soft">(no card on file to charge; add it to a bill at checkout, or waive it)</span>
            <Button variant="quiet" busy={waive.isPending && waive.variables === f.paymentId} onClick={() => window.confirm("Waive this fee? It won't be flagged again.") && waive.mutate(f.paymentId)}>
              Waive
            </Button>
          </div>
        ))}
      </div>
    </Alert>
  );
}
